/**
 * APIT-655 — the therapist's two signature-block overrides, as the signing preview
 * needs to understand them.
 *
 * A therapist may have a free-text override for the signature line, the credentials
 * line, or both: a licence can be held in a maiden name while the account is in a
 * married one, and Reliant's required credential format
 * (`PT IL 456789, PT NY 78946`) cannot be produced by the generator at all.
 *
 * ## Why this lives in the portal at all
 *
 * The preview exists so a therapist sees what they are about to sign. If it renders
 * the generated name while the PDF renders an override, the preview is worse than
 * useless — it is a false assurance on a clinical document. So the portal has to
 * apply the same three-state rule the renderer does:
 *
 * | stored              | result                                              |
 * |---------------------|-----------------------------------------------------|
 * | `null` / absent     | the default line — exactly today's preview          |
 * | `__BLANK__`         | no line at all; the block is deliberately blank     |
 * | any other string    | that string, split on `,` and newline into lines    |
 *
 * ## Kept deliberately in step with reform-sign
 *
 * This is a hand-copy of `resolve_override_lines` and `split_override_lines` in
 * `reform_sign/pdf/sign.py`, which is the authority. It is duplicated rather than
 * shared because the two live in different languages in different repositories, so
 * the guard against drift has to be the tests: every rule below is asserted against
 * the same cases the renderer's own suite uses, including the two that are easy to
 * get backwards — an empty string means *absent*, not suppressed, and a comma and a
 * newline are equally line breaks and may be mixed.
 */

/**
 * The suppression sentinel. Exact and case-sensitive, mirroring
 * `SUPPRESS_LINE_SENTINEL` in reform-sign and `SignatureConstants.BLANK_LINE_SENTINEL`
 * in restore-core.
 *
 * Internal: it must never be rendered. Everything in this module exists so that the
 * components never have to compare against it themselves and never hold it in a
 * string they are about to print.
 */
export const BLANK_LINE_SENTINEL = '__BLANK__';

export type ResolvedLines = {
  /** The lines to draw. Empty when the line is suppressed. */
  lines: string[];
  /** True when an override applied at all — false means "behave exactly as before". */
  overridden: boolean;
  /** True only for the sentinel: deliberately blank, as opposed to simply absent. */
  suppressed: boolean;
};

/**
 * Split an override into display lines, the way the renderer does.
 *
 * Both a comma and a newline are line breaks, either may be used and they may be
 * mixed; each segment is trimmed and empty segments are dropped, so
 * `'Tom Smith, , PT IL 456789\n'` yields two lines.
 */
export const splitOverrideLines = (value?: string | null): string[] =>
  (value ?? '')
    .split(/[,\n]/)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

/**
 * Resolve one override against the line that would otherwise be drawn.
 *
 * An empty string is treated as absent rather than as a suppression request —
 * upstream normalises `''` to `null`, so a `''` that arrives carries no intent, and
 * falling back to the default is the conservative reading. A credential line that
 * silently vanishes from a document a therapist is about to sign is a worse failure
 * than one that renders as it always has.
 */
export const resolveOverrideLines = (
  override: string | null | undefined,
  defaultLines: string[]
): ResolvedLines => {
  if (!override) {
    return { lines: defaultLines, overridden: false, suppressed: false };
  }
  if (override === BLANK_LINE_SENTINEL) {
    return { lines: [], overridden: true, suppressed: true };
  }
  return {
    lines: splitOverrideLines(override),
    overridden: true,
    suppressed: false,
  };
};

/**
 * The default credentials line: what the block shows with no override.
 *
 * Composed here rather than inline so it can be handed to `resolveOverrideLines` as
 * a default. An empty result means neither part is present, which is the case the
 * component must render as no line at all — the same guard the block had before this
 * feature existed.
 */
export const composeCredentialLine = (
  credentials?: string | null,
  license?: string | null
): string => {
  const parts = [credentials, license ? `Lic. ${license}` : ''].filter(
    (part) => Boolean(part) && String(part).length > 0
  );
  return parts.join(' · ');
};

/** Shown in place of a suppressed signature line. */
export const SIGNATURE_BLANK_NOTE = 'Signature line intentionally blank';

/** Shown in place of a suppressed credentials line. */
export const CREDENTIALS_BLANK_NOTE = 'Credential line intentionally blank';
